/**
 * SharePointFolderModal - Prompt for SharePoint folder URL
 *
 * Used when generating work orders - PM specifies the project folder
 * where the WO should be saved.
 */

import { useState, useEffect } from "react";

export default function SharePointFolderModal({
  isOpen,
  onClose,
  onConfirm,
  projectName,
  quoteNumber,
  existingFolder,
}) {
  const [folderUrl, setFolderUrl] = useState("");
  const [error, setError] = useState("");

  // Pre-fill with existing folder if available
  useEffect(() => {
    if (isOpen && existingFolder) {
      setFolderUrl(existingFolder);
    }
  }, [isOpen, existingFolder]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setFolderUrl(existingFolder || "");
      setError("");
    }
  }, [isOpen, existingFolder]);

  const validateUrl = (url) => {
    if (!url.trim()) {
      return "Please enter a SharePoint folder URL";
    }
    if (!url.includes("sharepoint.com")) {
      return "URL must be a SharePoint link";
    }
    return null;
  };

  const handleConfirm = () => {
    const validationError = validateUrl(folderUrl);
    if (validationError) {
      setError(validationError);
      return;
    }
    onConfirm(folderUrl.trim());
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Select SharePoint Folder
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {quoteNumber} - {projectName}
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SharePoint Folder URL
            </label>
            <input
              type="url"
              value={folderUrl}
              onChange={(e) => {
                setFolderUrl(e.target.value);
                setError("");
              }}
              placeholder="https://3dcolor.sharepoint.com/sites/..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                error ? "border-red-500" : "border-gray-300"
              }`}
              autoFocus
            />
            {error && (
              <p className="mt-1 text-sm text-red-600">{error}</p>
            )}

            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Open the project folder in SharePoint,
                copy the URL from your browser, and paste it here.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Generate Work Order
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
